import { FC, ReactNode, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { useQuery } from 'react-query'
import { KTIcon } from '../../../_metronic/helpers'
import { PageLink, PageTitle, useLayout } from '../../../_metronic/layout/core'
import { Column, useFilters, useGlobalFilter, useSortBy, useTable } from 'react-table'
import { axiosClientAuth } from '../../utils'
import moment from 'moment'

const MANAGE_TEMPLATE_QUERY_KEY = 'template-mappings'

const URL_REGEX = /(https?:\/\/[^\s]+)/g

function highlightUrls(content: string): ReactNode {
  const parts = content.split(URL_REGEX)
  return parts.map((part, i) =>
    URL_REGEX.test(part) ? (
      <span key={i} style={{ color: '#009ef7', fontWeight: 600, wordBreak: 'break-all' }}>
        {part}
      </span>
    ) : (
      part
    )
  )
}

const ManageTemplate: FC = () => {
  const intl = useIntl()
  const { manageTemplateDateFilter, statusFilter, manageTemplatePeidFilter, searchQuery } =
    useLayout()
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  useEffect(() => {
    setPage(1)
  }, [manageTemplateDateFilter, statusFilter, manageTemplatePeidFilter, searchQuery, pageSize])

  const { data, isFetching } = useQuery(
    [
      MANAGE_TEMPLATE_QUERY_KEY,
      { page, pageSize, manageTemplateDateFilter, statusFilter, manageTemplatePeidFilter, searchQuery },
    ],
    () =>
      axiosClientAuth
        .get('/urls/template-mappings/', {
          params: {
            page,
            page_size: pageSize,
            from_date: manageTemplateDateFilter?.[0] || undefined,
            to_date: manageTemplateDateFilter?.[1] || undefined,
            template_mapping_status: statusFilter || undefined,
            owner_id: manageTemplatePeidFilter || undefined,
            search: searchQuery || undefined,
          },
          transformResponse: [
            (raw: string) =>
              JSON.parse(raw.replace(/"owner_id"\s*:\s*(\d{15,})/g, '"owner_id":"$1"')),
          ],
        })
        .then((res) => res.data.data)
  )

  const results = data?.results ?? []
  const totalCount = data?.count ?? 0
  const pageCount = Math.ceil(totalCount / pageSize) || 1

  const columns: ReadonlyArray<Column<any>> = useMemo(
    () => [
      {
        Header: intl.formatMessage({ id: 'TABLE.SL_NO', defaultMessage: 'S.No' }),
        id: 'serial-number',
        Cell: ({ row }: any) => (page - 1) * pageSize + row.index + 1,
      },
      {
        Header: intl.formatMessage({ id: 'TABLE.TEMPLATE', defaultMessage: 'TEMPLATE' }),
        id: 'name',
        Cell: ({ row }: any) => (
          <>
            <span>{row.original.template?.title ?? '--'}</span>
            <span className='d-block text-muted fw-semibold'>
              {row.original.template?.id ?? '--'}
            </span>
          </>
        ),
      },
      {
        Header: intl.formatMessage({ id: 'TABLE.SENDER_NAME', defaultMessage: 'Sender Name' }),
        id: 'sender',
        Cell: ({ row }: any) => (
          <>
            <span>{row.original.template?.header?.title ?? '--'}</span>
            <span className='d-block text-muted fw-semibold'>
              {row.original.template?.header?.id ?? '--'}
            </span>
          </>
        ),
      },
      {
        Header: intl.formatMessage({ id: 'TABLE.PE_DETAILS', defaultMessage: 'PE Details' }),
        id: 'pe-details',
        Cell: ({ row }: any) => <span>{row.original.owner_id ?? '--'}</span>,
      },
      {
        Header: intl.formatMessage({ id: 'TABLE.MAPPED_CONTENT', defaultMessage: 'Mapped Content' }),
        id: 'mapped-content',
        Cell: ({ row }: any) => (
          <span
            style={{ whiteSpace: 'normal', wordBreak: 'break-word', maxWidth: '300px', display: 'block' }}
          >
            {row.original.content ? highlightUrls(row.original.content) : '--'}
          </span>
        ),
      },
      {
        Header: intl.formatMessage({ id: 'TABLE.DATE', defaultMessage: 'DATE' }),
        id: 'date',
        Cell: ({ row }: any) => (
          <>
            <span>{moment(row.original.created_at).format('DD/MM/YYYY')}</span>
            <span className='d-block text-muted fw-semibold'>
              {moment(row.original.created_at).format('LT')}
            </span>
          </>
        ),
      },
      {
        Header: intl.formatMessage({ id: 'TABLE.STATUS', defaultMessage: 'Status' }),
        id: 'status',
        Cell: ({ row }: any) => {
          const active = row.original.status?.self?.active
          return active === true ? (
            <span className='badge badge-light-success'>
              {intl.formatMessage({ id: 'COMMON.ENABLED', defaultMessage: 'Enabled' })}
            </span>
          ) : active === false ? (
            <span className='badge badge-light-danger'>
              {intl.formatMessage({ id: 'COMMON.DISABLED', defaultMessage: 'Disabled' })}
            </span>
          ) : (
            <span className='badge badge-light-warning'>
              {intl.formatMessage({ id: 'COMMON.PENDING', defaultMessage: 'Pending' })}
            </span>
          )
        },
      },
    ],
    [page, pageSize, intl]
  )

  const table = useTable(
    {
      columns: columns as readonly Column<object>[],
      data: results,
      autoResetFilters: false,
      autoResetGlobalFilter: false,
      autoResetSortBy: false,
    },
    useFilters,
    useGlobalFilter,
    useSortBy
  )

  return (
    <div className='row'>
      <div className='card card-flush'>
        <div className='card-header mt-5'>
          <div className='card-title flex-column'>
            <h3 className='fw-bold mb-1'>
              {intl.formatMessage({ id: 'PAGE.MANAGE_TEMPLATE', defaultMessage: 'URL Mapped Templates' })}
            </h3>
            <div className='fs-6 text-gray-400'>
              {intl.formatMessage(
                { id: 'COMMON.TOTAL_RECORDS', defaultMessage: 'Total Records: {total}' },
                { total: totalCount }
              )}
            </div>
          </div>
        </div>
        <div className='card-body'>
          <div className='table-responsive'>
            <table
              {...table.getTableProps()}
              className='table align-middle table-row-dashed fs-6 gy-5 dataTable no-footer'
            >
              <thead>
                {table.headerGroups.map((headerGroup, index) => (
                  <tr {...headerGroup.getHeaderGroupProps()} key={`mt-header-${index}`}>
                    {headerGroup.headers.map((column) => (
                      <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                        <span className='text-gray-400 fs-7'>{column.render('Header')}</span>
                        {column.isSorted ? (
                          column.isSortedDesc ? (
                            <KTIcon iconName='down' className='ms-1' />
                          ) : (
                            <KTIcon iconName='up' className='ms-1' />
                          )
                        ) : (
                          ''
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...table.getTableBodyProps()}>
                {isFetching ? (
                  <tr>
                    <td colSpan={7} className='text-center py-10'>
                      <span className='spinner-border spinner-border-sm text-primary' />
                    </td>
                  </tr>
                ) : (table.rows.length > 0 &&
                    table.rows.map((row) => {
                      table.prepareRow(row)
                      return (
                        <tr {...row.getRowProps()}>
                          {row.cells.map((cell) => (
                            <td className='fw-bold' {...cell.getCellProps()}>
                              {cell.render('Cell')}
                            </td>
                          ))}
                        </tr>
                      )
                    })) || (
                    <tr>
                      <td colSpan={7} className='text-center'>
                        <span className='fw-bold'>
                          {intl.formatMessage({ id: 'COMMON.NO_DATA_FOUND', defaultMessage: 'No data found.' })}
                        </span>
                      </td>
                    </tr>
                  )}
              </tbody>
            </table>
            <div className='d-flex justify-content-between align-items-center mt-5'>
              <div>
                <select
                  className='form-select form-select-sm form-select-solid'
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(parseInt(e.target.value))
                    setPage(1)
                  }}
                >
                  {[10, 25, 50, 100].map((item) => (
                    <option key={item}>{item}</option>
                  ))}
                </select>
              </div>
              <div>
                <span>
                  {intl.formatMessage(
                    { id: 'COMMON.PAGE_OF', defaultMessage: 'Page {page} of {total}' },
                    { page, total: pageCount }
                  )}
                </span>
              </div>
              <div>
                <button
                  className='btn btn-sm'
                  onClick={() => setPage((p) => p - 1)}
                  disabled={page <= 1}
                >
                  <KTIcon iconName='left' className='text-dark fs-1 me-5' />
                </button>
                <button
                  className='btn btn-sm'
                  onClick={() => setPage((p) => p + 1)}
                  disabled={page >= pageCount}
                >
                  <KTIcon iconName='right' className='text-dark fs-1 me-5' />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const ManageTemplateWrapper: FC = () => {
  const intl = useIntl()
  const { setRightTolbar } = useLayout()

  useEffect(() => {
    setRightTolbar('managetemplate')
  }, [setRightTolbar])

  const breadcrumbs: Array<PageLink> = [
    {
      title: intl.formatMessage({ id: 'COMMON.HOME', defaultMessage: 'Home' }),
      path: '/dashboard',
      isSeparator: false,
      isActive: false,
    },
    {
      title: '',
      path: '',
      isSeparator: true,
      isActive: false,
    },
  ]

  return (
    <>
      <PageTitle breadcrumbs={breadcrumbs}>
        {intl.formatMessage({ id: 'PAGE.MANAGE_TEMPLATE', defaultMessage: 'URL Mapped Templates' })}
      </PageTitle>
      <ManageTemplate />
    </>
  )
}

export { ManageTemplateWrapper }
